"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyCloudBundle,
  fetchMe,
  pullCloud,
  pushCloud,
  readLocalBundle,
  reconcileAfterAuth,
  setCloudEnabled,
  type AuthUser,
} from "@/lib/cloud-sync";

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  syncing: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  syncNow: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    const me = await fetchMe();
    setUser(me);
    setCloudEnabled(!!me);
    if (me) {
      setSyncing(true);
      try {
        await reconcileAfterAuth();
      } finally {
        setSyncing(false);
      }
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
      credentials: "same-origin",
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Connexion impossible");
    setUser(data.user);
    setCloudEnabled(true);
    setSyncing(true);
    try {
      await reconcileAfterAuth();
    } finally {
      setSyncing(false);
    }
  }, []);

  const register = useCallback(
    async (email: string, password: string, displayName?: string) => {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, displayName }),
        credentials: "same-origin",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Inscription impossible");
      setUser(data.user);
      setCloudEnabled(true);
      setSyncing(true);
      try {
        await reconcileAfterAuth();
      } finally {
        setSyncing(false);
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
    setCloudEnabled(false);
    setUser(null);
  }, []);

  const syncNow = useCallback(async () => {
    if (!user) return;
    setSyncing(true);
    try {
      await pushCloud(readLocalBundle());
      const remote = await pullCloud();
      applyCloudBundle(remote);
    } finally {
      setSyncing(false);
    }
  }, [user]);

  const value = useMemo(
    () => ({ user, loading, syncing, refresh, login, register, logout, syncNow }),
    [user, loading, syncing, refresh, login, register, logout, syncNow],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans AuthProvider");
  return ctx;
}
